// Depth stand-ins for the passes that draw no colour.
//
// Two passes a frame want nothing but depth: the sun's shadow map and the
// volumetric clouds' prepass. Both walk the same meshes the colour pass does,
// so a vessel of eight parts costs sixty draw calls in each of them for
// geometry whose material they never read, and the launch complex another
// twenty. A proxy is one merged geometry per rigid group, drawn once in
// place of all of them.
//
// It lives on its own layer, so:
//   - the colour pass never sees it (the local camera keeps layer 0),
//   - the raycasts that pick parts and buildings never hit it,
//   - the shadow map and the prepass do, because they turn the layer on for
//     the camera they test against (withDepthLayer).
// The meshes it stands in for keep their geometry and their materials; they
// only stop casting shadows, and the prepass hides them.
//
// Anything that moves inside a group keeps its own draws: the proxy is built
// from the group's own meshes, never from a sub-assembly on a pivot (a
// gimbal, a leg, a fairing half), and a group whose meshes have since been
// hidden falls back to them.

import * as THREE from 'three';

export const PROXY_LAYER = 7;

const proxies = new Set();
const cache = new Map();
// A nested shadow pass can run while the cloud prepass has hidden sources.
// Those meshes are still live; their visibility is only scoped to the pass.
const hiddenSources = new WeakSet();
// One material for every proxy: the shadow map reads its side (front, like
// the surfaces it replaces, so three renders back faces into the map exactly
// as it did), and the prepass reads its depth flags.
const PROXY_MATERIAL = new THREE.MeshBasicMaterial({ name: 'depthProxy', side: THREE.FrontSide });

// Meshes of `group` a proxy can stand in for: its own children, opaque,
// front-facing, shadow-casting, with no alpha cutout and no instancing.
function sources(group) {
  const out = [];
  for (const o of group.children) {
    if (!o.isMesh || o.isInstancedMesh || !o.castShadow || !o.visible) continue;
    const m = o.material;
    if (!m || Array.isArray(m)) continue;
    if (m.transparent || m.alphaTest > 0 || m.alphaMap || m.side !== THREE.FrontSide) continue;
    if (!o.geometry || !o.geometry.attributes.position) continue;
    if (o.userData.depthProxy || o.userData.noCloudDepth) continue;
    out.push(o);
  }
  return out;
}

function mergePositions(list) {
  let nv = 0, ni = 0;
  for (const m of list) {
    const g = m.geometry;
    nv += g.attributes.position.count;
    ni += g.index ? g.index.count : g.attributes.position.count;
  }
  const pos = new Float32Array(nv * 3);
  const idx = nv > 65535 ? new Uint32Array(ni) : new Uint16Array(ni);
  const v = new THREE.Vector3();
  let vo = 0, io = 0;
  for (const m of list) {
    const g = m.geometry;
    const p = g.attributes.position;
    m.updateMatrix();
    const hasM = !m.matrix.equals(_identity);
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i);
      if (hasM) v.applyMatrix4(m.matrix);
      pos[(vo + i) * 3] = v.x; pos[(vo + i) * 3 + 1] = v.y; pos[(vo + i) * 3 + 2] = v.z;
    }
    if (g.index) {
      const a = g.index.array;
      for (let i = 0; i < a.length; i++) idx[io + i] = a[i] + vo;
      io += a.length;
    } else {
      for (let i = 0; i < p.count; i++) idx[io + i] = vo + i;
      io += p.count;
    }
    vo += p.count;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  geo.computeBoundingSphere();
  return geo;
}
const _identity = new THREE.Matrix4();

// Give `group` a depth proxy for its own static meshes. `key` caches the
// merged geometry (part ids are deterministic; anything built from terrain
// heights must not pass one). Returns the proxy, or null when there is
// nothing worth merging.
export function attachDepthProxy(group, key = null) {
  if (!group || group.userData.depthProxyMesh) return null;
  const list = sources(group);
  if (list.length < 2) return null;
  let geo = key ? cache.get(key) : null;
  if (!geo) {
    geo = mergePositions(list);
    geo.userData.shared = !!key;
    if (key) cache.set(key, geo);
  }
  const mesh = new THREE.Mesh(geo, PROXY_MATERIAL);
  mesh.name = 'depthProxy';
  mesh.castShadow = true;
  mesh.receiveShadow = false;
  mesh.layers.set(PROXY_LAYER);
  mesh.userData.depthProxy = true;
  mesh.userData.sources = list;
  for (const m of list) { m.castShadow = false; m.userData.depthProxied = true; }
  group.add(mesh);
  group.userData.depthProxyMesh = mesh;
  proxies.add(mesh);
  return mesh;
}

// A group whose meshes have been hidden since (a jettisoned shroud, a
// collapsed canopy) hands the work back to them.
function live(mesh) {
  const list = mesh.userData.sources;
  for (const m of list) if (!m.visible && !hiddenSources.has(m)) return false;
  return true;
}

// Off switch, so a measurement or a capture can compare the frame with the
// proxies and with the meshes drawing for themselves.
let enabled = true;
export function setDepthProxies(on) {
  on = !!on;
  if (on === enabled) return;
  enabled = on;
  for (const p of proxies) {
    p.visible = on;
    for (const m of p.userData.sources) m.castShadow = !on;
  }
}

// Run fn with `camera` able to see the proxies. `hideSources` takes the
// meshes a live proxy stands in for out of the pass, which the prepass needs
// (it draws geometry); the shadow map needs nothing, since a proxied mesh no
// longer casts. Everything is put back afterwards.
export function withDepthLayer(camera, fn, { hideSources = false } = {}) {
  if (!enabled) return fn();
  const previousMask = camera.layers.mask;
  const stale = [];
  const hidden = [];
  for (const p of proxies) {
    if (live(p)) {
      if (hideSources) for (const m of p.userData.sources) {
        if (!m.visible) continue;
        m.visible = false;
        hiddenSources.add(m);
        hidden.push(m);
      }
    } else {
      // Fall back: this group's meshes cast for themselves again.
      stale.push({ proxy: p, visible: p.visible, shadows: p.userData.sources.map(m => m.castShadow) });
      p.visible = false;
      for (const m of p.userData.sources) m.castShadow = true;
    }
  }
  camera.layers.enable(PROXY_LAYER);
  try {
    return fn();
  } finally {
    // renderer.render() invokes the shadow wrapper inside the cloud pass.
    // Preserve the outer pass's layer, or its queued proxies are skipped
    // when Three checks layers again before drawing the depth geometry.
    camera.layers.mask = previousMask;
    for (const m of hidden) { m.visible = true; hiddenSources.delete(m); }
    for (const { proxy, visible, shadows } of stale) {
      proxy.visible = visible;
      proxy.userData.sources.forEach((m, i) => { m.castShadow = shadows[i]; });
    }
  }
}

// Forget the proxies under `root` (a vessel or a part on its way out), so a
// disposed group leaves nothing behind for the two passes to walk.
export function releaseDepthProxies(root) {
  if (!root) return;
  root.traverse((o) => {
    if (!o.userData.depthProxy) return;
    proxies.delete(o);
    if (o.geometry && !o.geometry.userData.shared) o.geometry.dispose();
  });
}

// Every proxy that is still in a scene, for the tests.
export function depthProxyCount() { return proxies.size; }
